import {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {InfinityScroll} from '../../design-system/atoms/InfinityScroll/InfinityScroll';
import {Loading} from '../../design-system/atoms/Loading/Loading';
import {
  DayOfWeekBmType,
  StaticLandingDayOfWeekSection,
  StaticLandingDayOfWeekSectionQueryVariables,
} from '../../graphql/generated/types';
import {onSuccessStaticLandingSection} from '../../pagewebLayout/PagewebLayoutSlice';
import {CACHE_ONLY_CONFIG} from '../../reactQuery/constant';
import {useInfiniteStaticLandingDayOfWeekSectionQuery} from '../../wrapper/landingWrapper';
import useTypedSelector from '../useTypedSelector';
import {StaticLandingDayOfWeekMoreFetcherProps} from './StaticLandingDayOfWeekMoreFetcherProps.interface';

export function StaticLandingDayOfWeekMoreFetcher({
  children,
  section,
}: StaticLandingDayOfWeekMoreFetcherProps) {
  const dispatch = useDispatch();
  const landingDayOfWeekSection = useTypedSelector(
    state =>
      state.pagewebLayout.entities.sections[section.id] as Omit<
        StaticLandingDayOfWeekSection,
        'groups'
      >
  );

  const {param: currentParam} = landingDayOfWeekSection;
  const param: StaticLandingDayOfWeekSectionQueryVariables['param'] = {
    categoryUid: landingDayOfWeekSection.param?.categoryUid,
    bmType: currentParam?.businessModel?.param as DayOfWeekBmType,
    subcategoryUid: currentParam?.subcategory?.param,
    dayTabUid: currentParam?.dayTab?.param,
    screenUid: currentParam?.screenUid,
  };

  const {data, isSuccess, fetchNextPage, isFetching, isFetchedAfterMount} =
    useInfiniteStaticLandingDayOfWeekSectionQuery(
      {
        sectionId: section.id,
        param,
      },
      {
        initialPageParam: {
          sectionId: section.id,
          param,
        },
        getNextPageParam: lastPage => {
          const staticLandingDayofWeekSection =
            lastPage.staticLandingDayOfWeekSection as StaticLandingDayOfWeekSection;
          return staticLandingDayofWeekSection?.isEnd
            ? undefined
            : {
                sectionId: section.id,
                param: {
                  ...param,
                  page: (staticLandingDayofWeekSection.param.page || 0) + 1,
                },
              };
        },
        initialData: landingDayOfWeekSection
          ? {
              pages: [
                {
                  staticLandingDayOfWeekSection:
                    landingDayOfWeekSection as StaticLandingDayOfWeekSection,
                },
              ],
              pageParams: [{sectionId: section.id, param}],
            }
          : undefined,
        ...CACHE_ONLY_CONFIG,
      }
    );

  const lastPage = data?.pages[data.pages.length - 1]
    ?.staticLandingDayOfWeekSection as
    | StaticLandingDayOfWeekSection
    | undefined;
  useEffect(() => {
    if (isFetchedAfterMount && isSuccess) {
      dispatch(
        onSuccessStaticLandingSection(lastPage, {
          sectionId: section.id,
          page: lastPage?.param.page || 0,
        })
      );
    }
  }, [dispatch, isFetchedAfterMount, isSuccess, lastPage, section.id]);

  return (
    <>
      <InfinityScroll
        isLoading={isFetching}
        canLoadNextPage={!isFetching && !lastPage?.isEnd}
        requestNextPageData={fetchNextPage}
      >
        {children}
      </InfinityScroll>
      {isFetching && (
        <div className="flex-center-full">
          <Loading />
        </div>
      )}
    </>
  );
}
